import * as THREE from 'three';
import { bandit, type BanditRole } from '../../../content/encounters/bandits.js';
import { BLACK_HOOD } from '../../../content/encounters/gangs.js';
import { samplePath } from '../../../shared/game/path-curve.js';
import { terrainHeight } from '../../../shared/game/terrain.js';
import type { EnemyDefinition, MapDefinition, Point, UnitState } from '../../../shared/game/world-types.js';
import { createUnitVisual } from './unit-visual.js';
import type { UnitAnimator } from './unit-animator.js';

/** The gang that tramps down the crossroads behind the sign-in screen: captain at the head, a hunter trailing. */
const CREW: { role: BanditRole; lateral: number }[] = [
  { role: 'captain', lateral: -.6 }, { role: 'bruiser', lateral: 1.1 }, { role: 'bandit', lateral: -1.2 },
  { role: 'bandit', lateral: .5 }, { role: 'assassin', lateral: -.3 }, { role: 'hunter', lateral: .9 },
];
const SPACING = 2.4, REACH = 40, ENTRY = 26, CLOTH = 0x6e3b32, STROLL = 1.6, SLOWDOWN = .25;
/** The crew ambles at a quarter of its content speed. */
const PACE = STROLL * SLOWDOWN;
/**
 * The pace the crew's legs are posed for, as a share of their walk speed: a patrol's easy stroll, well inside the stroll's range (at half
 * walk speed or less, see `STROLL_FULL` in `character-animator.ts`), so the short step holds round the road's bends.
 */
const STEP_PACE = .35;

/** The crew, standing at `spawn`. */
export function lobbyCrew(spawn: Point = { x: 0, z: 0 }): EnemyDefinition[] {
  return CREW.map(({ role }, i) => {
    const { title, ...unit } = bandit(role, CLOTH, i + 2, BLACK_HOOD);
    return { ...unit, id: `splash-bandit-${i}`, name: title, spawn, speed: PACE / STEP_PACE };
  });
}

/**
 * A client-only projection that walks bandits along the road through the arrival crossroads while nobody is in a room.
 * It carries no game state, is never sent to or from the server and disappears the moment a party enters the world.
 */
export class LobbyPatrol {
  readonly ready: Promise<void>;
  private readonly road: Point[];
  private readonly lengths: number[] = [];
  private readonly members: { group: THREE.Group; animator: UnitAnimator; definition: EnemyDefinition; lateral: number; place: number; walking: boolean }[] = [];
  private readonly total: number;
  private readonly start: number;
  private readonly centre: number;
  private began: number | undefined;
  /** Which walk-in this is, counting from 0 each time the splash shows; -1 before the first. */
  lap = -1;

  constructor(private scene: THREE.Scene, private map: MapDefinition) {
    // The east-west road through the origin runs toward the right of the screen, clear of the sign-in card; follow its drawn curve.
    const roads = (map.scenery?.paths ?? []).filter(path => path.kind === 'road');
    const reach = (points: Point[]) => Math.max(...points.map(point => point.x)) - Math.min(...points.map(point => point.x));
    const chosen = roads.reduce((best, path) => (reach(path.points) > reach(best.points) ? path : best), roads[0]);
    // Dense enough (about half a metre on the longest span) that the crew's heading and side offset turn smoothly between samples:
    // a coarse polyline makes each member hop sideways and snap round at every sample.
    this.road = chosen ? samplePath(chosen.points, (chosen.points.length - 1) * Math.ceil(longestSpan(chosen.points) / .5)) : [{ x: -REACH, z: 0 }, { x: REACH, z: 0 }];
    let total = 0; this.lengths.push(0);
    for (let i = 1; i < this.road.length; i++) { total += Math.hypot(this.road[i].x - this.road[i - 1].x, this.road[i].z - this.road[i - 1].z); this.lengths.push(total); }
    this.total = total;
    const centre = this.lengths[this.road.reduce((best, point, i) => (Math.hypot(point.x, point.z) < Math.hypot(this.road[best].x, this.road[best].z) ? i : best), 0)];
    this.centre = centre;
    this.start = Math.max(0, centre - ENTRY); // The column steps onto the road just off-screen, so the walk-in starts soon after the splash opens.
    const pending: Promise<void>[] = [];
    lobbyCrew(this.road[0]).forEach((definition, i) => {
      const { lateral } = CREW[i], visual = createUnitVisual(definition);
      if (map.terrain) visual.animator.groundHeight = (x, z) => terrainHeight(map, x, z);
      pending.push(visual.ready);
      visual.ring.visible = false; visual.group.visible = false; // The plate and selection ring belong to the game proper.
      this.scene.add(visual.group);
      this.members.push({ group: visual.group, animator: visual.animator, definition, lateral, place: i * SPACING, walking: false });
    });
    this.ready = Promise.all(pending).then(() => {});
  }

  /** Advances the walk to `seconds`; `shown` is false once a party has a snapshot or has chosen its heroes. */
  update(seconds: number, shown: boolean, inView?: (position: THREE.Vector3, height: number) => boolean) {
    const span = Math.min(this.total - this.start, ENTRY + REACH), loop = span + CREW.length * SPACING + 12;
    // The walk-in begins when the splash is first shown, not while it is hidden behind the door or the party screens.
    if (!shown) { this.began = undefined; this.lap = -1; } else this.began ??= seconds;
    // The column enters at the far edge of its stretch of road and walks in.
    const travelled = ((seconds - (this.began ?? seconds)) * PACE), head = travelled % loop;
    if (shown) this.lap = Math.floor(travelled / loop);
    for (const member of this.members) {
      const along = Math.min(span, Math.max(0, head - member.place)), onRoad = head - member.place > 0 && head - member.place < span;
      member.walking = shown && onRoad; member.group.visible = false;
      if (!member.walking) continue;
      const point = this.at(this.start + along, member.lateral);
      member.group.position.set(point.x, terrainHeight(this.map, point.x, point.z), point.z);
      // Waiting off the road, or walking where neither the view nor the sun's shadow reaches, a member is not posed at all.
      member.group.visible = !inView || inView(member.group.position, member.animator.rig.height);
      if (member.group.visible) member.animator.update(this.state(member.definition), point, seconds, undefined, false);
    }
  }

  /** Where the crew's walking members are now; none while the walk is hidden. */
  crew(): Point[] { return this.members.filter(member => member.walking).map(member => ({ x: member.group.position.x, z: member.group.position.z })); }

  /** A spot on the road `ahead` metres past the crossroads (negative: before it), pushed `lateral` metres to the side. */
  spot(ahead: number, lateral: number): Point { return this.at(this.centre + ahead, lateral); }

  dispose() {
    for (const member of this.members) member.animator.dispose();
  }

  /** The road position `distance` meters along it, pushed `lateral` meters to the side. */
  private at(distance: number, lateral: number): Point {
    // The first sample at or past `distance` (clamped to the last span), found by halving.
    let low = 1, high = this.lengths.length - 1;
    while (low < high) { const middle = (low + high) >> 1; if (this.lengths[middle] < distance) low = middle + 1; else high = middle; }
    const i = low;
    const a = this.road[i - 1], b = this.road[i], run = Math.max(1e-6, this.lengths[i] - this.lengths[i - 1]), t = THREE.MathUtils.clamp((distance - this.lengths[i - 1]) / run, 0, 1);
    const dx = (b.x - a.x) / Math.hypot(b.x - a.x, b.z - a.z), dz = (b.z - a.z) / Math.hypot(b.x - a.x, b.z - a.z);
    return { x: a.x + (b.x - a.x) * t - dz * lateral, z: a.z + (b.z - a.z) * t + dx * lateral };
  }

  private state(definition: EnemyDefinition): UnitState {
    const { maxHp } = definition.profile;
    return { id: definition.id, name: definition.name, radius: definition.radius, x: 0, z: 0, route: [], kind: definition.kind,
      health: { hp: maxHp, maxHp, temporaryHp: 0, wounds: 0, maxWounds: 0, dying: false, dead: false },
      combat: { actions: 0, actionRecoveryRemaining: 0, rush: 0, rushDisadvantage: 0, targetId: null, attackId: null, order: 'idle', anticipating: false, opening: false } } as UnitState;
  }
}

const longestSpan = (points: Point[]) => Math.max(...points.slice(1).map((point, i) => Math.hypot(point.x - points[i].x, point.z - points[i].z)));
